import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Camera } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { useApp } from "@/context/AppContext";

const CAT_TILES = [
  { key: "fashion", label: "Fashion", tag: "01 / Wearables", img: "https://images.unsplash.com/photo-1664076458686-3449062080ac" },
  { key: "home", label: "Home Decor", tag: "02 / Interiors", img: "https://images.unsplash.com/photo-1724582586529-62622e50c0b3" },
  { key: "electronics", label: "Electronics", tag: "03 / Signal", img: "https://images.unsplash.com/photo-1583394838336-acd977736f90" },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const { setAssistantOpen, setVisualOpen } = useApp();

  useEffect(() => {
    api.get("/products?limit=8").then(({ data }) => setFeatured(data.products.slice(0, 8)));
  }, []);

  return (
    <div>
      {/* HERO — Editorial split */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-end">
          <div className="md:col-span-7 fade-up">
            <div className="font-mono-brand text-xs uppercase tracking-[0.3em] text-[#FF3B30] mb-6" data-testid="hero-tagline">
              Nova.system // ai-native retail // v1.0
            </div>
            <h1 className="font-display font-black text-6xl md:text-8xl leading-[0.9] tracking-tighter">
              A store<br />
              that <span className="italic font-light">knows</span><br />
              your taste.
            </h1>
            <p className="mt-8 text-lg text-neutral-600 max-w-lg leading-relaxed">
              Nova Market is a multi-brand house curated by an intelligent concierge.
              Chat, search by image, or let the machine style you.
            </p>
            <div className="flex flex-wrap gap-3 mt-10">
              <Link
                to="/shop/all"
                className="inline-flex items-center gap-2 bg-black text-white px-6 py-3 font-mono-brand text-xs uppercase tracking-[0.2em] hover:bg-[#FF3B30] transition-colors"
                data-testid="hero-shop-btn"
              >
                Enter the store <ArrowRight className="w-3 h-3" />
              </Link>
              <button
                onClick={() => setAssistantOpen(true)}
                className="inline-flex items-center gap-2 border border-black px-6 py-3 font-mono-brand text-xs uppercase tracking-[0.2em] hover:bg-black hover:text-white transition-colors"
                data-testid="hero-ai-btn"
              >
                <Sparkles className="w-3 h-3" /> Ask Nova
              </button>
              <button
                onClick={() => setVisualOpen(true)}
                className="inline-flex items-center gap-2 border border-[#FF3B30] text-[#FF3B30] px-6 py-3 font-mono-brand text-xs uppercase tracking-[0.2em] hover:bg-[#FF3B30] hover:text-white transition-colors"
                data-testid="hero-visual-btn"
              >
                <Camera className="w-3 h-3" /> Visual search
              </button>
            </div>
          </div>

          <div className="md:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className="relative aspect-[4/5] overflow-hidden"
            >
              <img
                src="https://images.unsplash.com/photo-1664076458686-3449062080ac?auto=format&fit=crop&w=1000&q=80"
                alt="Editorial"
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-4 left-4 font-mono-brand text-[10px] uppercase tracking-[0.2em] text-white bg-black/60 backdrop-blur px-2 py-1">
                No. 01 / Editorial FW26
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CATEGORIES — Tetris bento */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-24">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="font-mono-brand text-xs uppercase tracking-[0.3em] text-neutral-500">/ departments</div>
            <h2 className="font-display font-black text-4xl md:text-5xl leading-none tracking-tighter mt-2">Three axes of desire.</h2>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {CAT_TILES.map((c, i) => (
            <Link
              key={c.key}
              to={`/shop/${c.key}`}
              className={`relative group overflow-hidden ${
                i === 0 ? "md:col-span-6 aspect-[4/5]" : i === 1 ? "md:col-span-6 aspect-[4/5]" : "md:col-span-12 aspect-[16/7]"
              }`}
              data-testid={`category-tile-${c.key}`}
            >
              <img
                src={c.img + "?auto=format&fit=crop&w=1600&q=80"}
                alt={c.label}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[900ms] ease-out"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              <div className="absolute inset-0 flex flex-col justify-end p-8">
                <div className="font-mono-brand text-[10px] uppercase tracking-[0.2em] text-white/80">{c.tag}</div>
                <div className="font-display font-black text-4xl md:text-6xl text-white leading-none tracking-tighter mt-2">
                  {c.label}
                </div>
                <div className="flex items-center gap-2 font-mono-brand text-xs uppercase tracking-[0.2em] text-white mt-4 opacity-0 group-hover:opacity-100 transition-opacity">
                  Browse <ArrowRight className="w-3 h-3" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-24">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="font-mono-brand text-xs uppercase tracking-[0.3em] text-neutral-500">/ selected by nova</div>
            <h2 className="font-display font-black text-4xl md:text-5xl leading-none tracking-tighter mt-2">This week's picks.</h2>
          </div>
          <Link to="/shop/all" className="hidden md:inline-flex items-center gap-2 font-mono-brand text-xs uppercase tracking-[0.2em] hover:text-[#FF3B30]">
            View all <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
          {featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </section>

      {/* AI CTA strip */}
      <section className="bg-black text-white">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <div className="font-mono-brand text-xs uppercase tracking-[0.3em] text-[#FF3B30]">/ nova.assist</div>
            <h2 className="font-display font-black text-4xl md:text-6xl leading-none tracking-tighter mt-4">
              Skip the browse.<br />
              <span className="italic font-light">Tell Nova what you want.</span>
            </h2>
          </div>
          <div className="md:col-span-4">
            <button
              onClick={() => setAssistantOpen(true)}
              className="w-full border border-white/30 hover:border-[#FF3B30] hover:text-[#FF3B30] px-8 py-6 font-mono-brand text-xs uppercase tracking-[0.2em] flex items-center justify-between transition-colors"
              data-testid="cta-ai-btn"
            >
              <span>Open concierge</span>
              <Sparkles className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
